/*
 * DROP theme — vanilla replacements for the Lovable React components.
 * Each init function is idempotent per element and re-runs when the theme editor
 * reloads a section.
 */
(() => {
  const DROP = window.DROP || { routes: {}, strings: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const once = (el, key) => {
    if (el.dataset[key]) return false;
    el.dataset[key] = "1";
    return true;
  };
  const pad = (n, len = 2) => String(n).padStart(len, "0");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (window.Shopify && window.Shopify.designMode) {
    document.documentElement.classList.add("shopify-design-mode");
  }

  /* Body scroll lock shared by the menu, search and cart. */
  const locks = new Set();
  const lock = (key, on) => {
    on ? locks.add(key) : locks.delete(key);
    document.body.style.overflow = locks.size ? "hidden" : "";
  };

  /* ---------------------------------------------------------------- */
  /* Announcement bar — AnnouncementBar.tsx                            */
  /* ---------------------------------------------------------------- */
  function initAnnouncements(root) {
    $$("[data-announcements]", root).forEach((bar) => {
      if (!once(bar, "init")) return;
      const items = $$("[data-announcement]", bar);
      if (items.length < 2) return;
      let index = 0;
      const interval = Number(bar.dataset.interval) || 4000;
      setInterval(() => {
        items[index].classList.add("hidden");
        items[index].removeAttribute("aria-live");
        index = (index + 1) % items.length;
        const next = items[index];
        next.classList.remove("hidden");
        next.setAttribute("aria-live", "polite");
        // Restart the crossfade animation.
        next.classList.remove("announcement-swap");
        void next.offsetWidth;
        next.classList.add("announcement-swap");
      }, interval);
    });
  }

  /* ---------------------------------------------------------------- */
  /* Hero video — phones only, never for reduced motion / data saver   */
  /* ---------------------------------------------------------------- */
  function initHeroVideo(root) {
    $$("[data-hero-video]", root).forEach((video) => {
      if (!once(video, "init")) return;
      const phone = window.matchMedia("(max-width: 767px)").matches;
      const saveData = navigator.connection && navigator.connection.saveData;
      if (!phone || reducedMotion || saveData) return;
      // Native autoplay (muted + inline) also resumes by itself when a background tab becomes visible.
      video.autoplay = true;
      video.preload = "auto";
      video.src = video.dataset.heroVideo;
      // The poster stays visible underneath until frames are actually advancing.
      const reveal = () => {
        if (video.currentTime > 0) {
          video.classList.replace("opacity-0", "opacity-100");
          video.removeEventListener("timeupdate", reveal);
        }
      };
      video.addEventListener("timeupdate", reveal);
      video.play().catch(() => {
        // Blocked (e.g. low-power mode): retry once the visitor interacts or returns to the tab.
        const retry = () => video.play().catch(() => {});
        document.addEventListener("visibilitychange", retry, { once: true });
        document.addEventListener("touchstart", retry, { once: true, passive: true });
      });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Scroll reveal — Reveal.tsx                                        */
  /* ---------------------------------------------------------------- */
  let revealObserver;
  function initReveal(root) {
    const nodes = $$("[data-reveal]:not(.reveal-in)", root);
    if (!("IntersectionObserver" in window) || window.Shopify?.designMode) {
      nodes.forEach((n) => n.classList.add("reveal-in"));
      return;
    }
    revealObserver ||= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-in");
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    nodes.forEach((n) => revealObserver.observe(n));
  }

  /* ---------------------------------------------------------------- */
  /* Mobile menu — Header.tsx                                          */
  /* ---------------------------------------------------------------- */
  function setMenu(open) {
    const menu = $("[data-mobile-menu]");
    if (!menu) return;
    menu.classList.toggle("hidden", !open);
    lock("menu", open);
  }

  /* ---------------------------------------------------------------- */
  /* Search overlay — SearchOverlay.tsx, backed by predictive search   */
  /* ---------------------------------------------------------------- */
  let searchTimer;
  let searchController;
  function setSearch(open) {
    const overlay = $("[data-search-overlay]");
    if (!overlay) return;
    overlay.classList.toggle("hidden", !open);
    lock("search", open);
    if (open) setTimeout(() => $("[data-search-input]", overlay)?.focus(), 30);
  }

  async function runSearch(term) {
    const results = $("[data-search-results]");
    if (!results) return;
    const q = term.trim();
    if (!q) {
      results.innerHTML = $("[data-search-start]").innerHTML;
      return;
    }
    searchController?.abort();
    searchController = new AbortController();
    const url =
      `${DROP.routes.predictiveSearch}?q=${encodeURIComponent(q)}` +
      `&resources[type]=product&resources[limit]=8&resources[options][fields]=title,product_type,body,variants.title` +
      `&section_id=predictive-search`;
    try {
      const res = await fetch(url, { signal: searchController.signal });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const section = doc.querySelector("#shopify-section-predictive-search") || doc.body;
      results.innerHTML = section.innerHTML;
    } catch (err) {
      if (err.name !== "AbortError") console.error(err);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Cart drawer — CartDrawer.tsx + cartStore.ts                       */
  /* ---------------------------------------------------------------- */
  function setCart(open) {
    const drawer = $("[data-cart-drawer]");
    if (!drawer) return;
    document.documentElement.classList.toggle("cart-is-open", open);
    drawer.setAttribute("aria-hidden", open ? "false" : "true");
    lock("cart", open);
  }

  function renderCartSection(sections) {
    const html = sections && sections["cart-drawer"];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const next = doc.querySelector("[data-cart-drawer-inner]");
    const current = $("[data-cart-drawer-inner]");
    if (next && current) {
      current.replaceWith(next);
      $$("[data-cart-count]").forEach((el) => (el.textContent = next.dataset.itemCount));
    }
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ ...body, sections: "cart-drawer", sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || DROP.strings.cartError);
    renderCartSection(data.sections);
    return data;
  }

  async function changeLine(line, quantity) {
    const panel = $("[data-cart-panel]");
    panel?.classList.add("opacity-60", "pointer-events-none");
    try {
      await cartRequest(DROP.routes.cartChange, { line, quantity });
    } catch (err) {
      const box = $("[data-cart-error]");
      if (box) {
        box.textContent = err.message;
        box.classList.remove("hidden");
      }
    } finally {
      panel?.classList.remove("opacity-60", "pointer-events-none");
    }
  }

  /* ---------------------------------------------------------------- */
  /* Product page — products.$handle.tsx + ProductGallery.tsx          */
  /* ---------------------------------------------------------------- */
  function initProduct(root) {
    $$("[data-product-page]", root).forEach((page) => {
      if (!once(page, "init")) return;
      const data = JSON.parse($("[data-product-json]", page).textContent);
      const form = $("[data-product-form]", page);
      const groups = $$("[data-option-group]", page);
      const idInput = $("[data-variant-id]", page);
      const priceEl = $("[data-product-price]", page);
      const dot = $("[data-availability-dot]", page);
      const availText = $("[data-availability-text]", page);
      const addBtn = $("[data-add-button]", page);
      const addLabel = $("[data-add-label]", page);
      const spinner = $("[data-add-spinner]", page);
      const message = $("[data-product-message]", page);
      const selected = {}; // position -> value
      const single = data.variants.length === 1 ? data.variants[0] : null;

      // Options with a single value are hidden in the UI but still part of the match.
      const optionCount = data.variants[0]?.options.length || 0;
      const shownPositions = groups.map((g) => Number(g.dataset.optionPosition));
      for (let p = 1; p <= optionCount; p++) {
        if (!shownPositions.includes(p)) selected[p] = data.variants[0].options[p - 1];
      }

      const showMessage = (text) => {
        if (!message) return;
        message.textContent = text || "";
        message.classList.toggle("hidden", !text);
      };

      const matchVariant = () =>
        single ||
        data.variants.find((v) => v.options.every((val, i) => selected[i + 1] === val)) ||
        null;

      // Mirrors variantForOption(): a value is available if some variant with that value
      // matches every *other* selected option.
      const variantFor = (position, value) =>
        data.variants.find((v) =>
          v.options.every((val, i) => {
            const p = i + 1;
            if (p === position) return val === value;
            return selected[p] ? selected[p] === val : true;
          }),
        );

      const render = () => {
        groups.forEach((group) => {
          const position = Number(group.dataset.optionPosition);
          $$("[data-option-value]", group).forEach((btn) => {
            const value = btn.dataset.optionValue;
            const variant = variantFor(position, value);
            btn.disabled = !variant || !variant.available;
            btn.setAttribute("aria-pressed", selected[position] === value ? "true" : "false");
          });
        });

        const variant = matchVariant();
        idInput.value = variant ? variant.id : "";
        if (variant) {
          priceEl.textContent = variant.price;
          showGalleryMedia(page, variant.media_id);
        }

        const soldOut = !data.productAvailable;
        dot.classList.toggle("bg-primary", !soldOut);
        dot.classList.toggle("bg-muted-foreground", soldOut);
        availText.textContent = soldOut
          ? DROP.strings.soldOut
          : variant && !variant.available
            ? DROP.strings.unavailable
            : DROP.strings.available;
      };

      groups.forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        group.addEventListener("click", (e) => {
          const btn = e.target.closest("[data-option-value]");
          if (!btn || btn.disabled) return;
          selected[position] = btn.dataset.optionValue;
          showMessage(null);
          render();
        });
      });

      $("[data-size-guide]", page)?.addEventListener("click", () => showMessage(DROP.strings.sizeGuidePending));

      form?.addEventListener("submit", async (e) => {
        e.preventDefault();
        const missing = groups.find((g) => !selected[Number(g.dataset.optionPosition)]);
        if (missing && !single) {
          showMessage(`${DROP.strings.selectOption} ${missing.dataset.optionName.toLowerCase()}`);
          return;
        }
        const variant = matchVariant();
        if (!variant || !variant.available) {
          showMessage(DROP.strings.comboUnavailable);
          return;
        }
        showMessage(null);
        addBtn.disabled = true;
        addLabel.classList.add("hidden");
        spinner.classList.remove("hidden");
        try {
          await cartRequest(DROP.routes.cartAdd, { items: [{ id: variant.id, quantity: 1 }] });
          setCart(true);
        } catch (err) {
          showMessage(err.message);
        } finally {
          addBtn.disabled = false;
          addLabel.classList.remove("hidden");
          spinner.classList.add("hidden");
        }
      });

      render();
      initGallery(page);
    });
  }

  /* ---------------------------------------------------------------- */
  /* Quick add — homepage feature sections (size radios → bag)         */
  /* ---------------------------------------------------------------- */
  function initQuickAdd(root) {
    $$("[data-quick-add]", root).forEach((form) => {
      if (!once(form, "init")) return;
      const button = $("[data-quick-add-button]", form);
      const label = $("[data-quick-add-label]", form);
      const message = $("[data-quick-add-message]", form);
      const showMessage = (text) => {
        message.textContent = text || "";
        message.classList.toggle("hidden", !text);
      };
      form.addEventListener("change", () => showMessage(null));
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const id = form.querySelector('[name="id"]:checked, input[type="hidden"][name="id"]')?.value;
        if (!id) {
          const legend = $("legend", form)?.textContent.trim().toLowerCase() || "size";
          showMessage(`${DROP.strings.selectOption} ${legend}`);
          return;
        }
        button.disabled = true;
        const text = label.textContent;
        label.textContent = "…";
        try {
          await cartRequest(DROP.routes.cartAdd, { items: [{ id: Number(id), quantity: 1 }] });
          setCart(true);
        } catch (err) {
          showMessage(err.message);
        } finally {
          button.disabled = false;
          label.textContent = text;
        }
      });
    });
  }

  function setGalleryIndex(page, index) {
    const mains = $$("[data-gallery-main]", page);
    if (!mains.length) return;
    mains.forEach((m, i) => m.classList.toggle("hidden", i !== index));
    $$("[data-gallery-thumb]", page).forEach((t, i) => {
      const active = i === index;
      t.setAttribute("aria-current", active ? "true" : "false");
      t.classList.toggle("border-primary", active);
      t.classList.toggle("border-transparent", !active);
      t.classList.toggle("hover:border-border", !active);
    });
    const counter = $("[data-gallery-counter]", page);
    if (counter) counter.textContent = `${pad(index + 1)} / ${counter.dataset.total}`;
  }

  function showGalleryMedia(page, mediaId) {
    if (!mediaId) return;
    const mains = $$("[data-gallery-main]", page);
    const index = mains.findIndex((m) => m.dataset.mediaId === String(mediaId));
    if (index < 0) return;
    setGalleryIndex(page, index);
    const rail = $("[data-gallery-rail]", page);
    if (rail && rail.offsetParent) {
      rail.scrollTo({ left: (rail.scrollWidth / mains.length) * index, behavior: reducedMotion ? "auto" : "smooth" });
    }
  }

  function initGallery(page) {
    $$("[data-gallery-thumb]", page).forEach((thumb) => {
      thumb.addEventListener("click", () => setGalleryIndex(page, Number(thumb.dataset.galleryThumb)));
    });
    const rail = $("[data-gallery-rail]", page);
    const dots = $$("[data-gallery-dot]", page);
    if (rail && dots.length) {
      rail.addEventListener(
        "scroll",
        () => {
          const i = Math.min(Math.round(rail.scrollLeft / (rail.scrollWidth / dots.length)), dots.length - 1);
          dots.forEach((d, j) => {
            d.classList.toggle("bg-primary", i === j);
            d.classList.toggle("bg-border", i !== j);
          });
        },
        { passive: true },
      );
    }
  }

  /* ---------------------------------------------------------------- */
  /* Product carousel — ProductCarousel.tsx (Embla)                    */
  /* ---------------------------------------------------------------- */
  // Embla is only downloaded on pages that actually have a carousel.
  let emblaLoading;
  const loadEmbla = () =>
    (emblaLoading ||= window.EmblaCarousel
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = DROP.assets.embla;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        }));

  function initCarousels(root) {
    const regions = $$("[data-product-carousel]", root);
    if (!regions.length) return;
    if (!window.EmblaCarousel) {
      loadEmbla().then(() => initCarousels(root));
      return;
    }
    regions.forEach((region) => {
      if (!once(region, "init")) return;
      const viewport = $("[data-carousel-viewport]", region);
      const slides = $$("[data-carousel-slide]", region);
      const bars = $$("[data-carousel-bar]", region);
      const counter = $("[data-carousel-counter]", region);
      const viewLink = $("[data-carousel-view]", region);
      const total = pad(slides.length);

      // Drop the pre-centering padding; Embla takes over positioning.
      $("[data-carousel-track]", region)?.classList.remove("pl-[12%]", "sm:pl-[18%]", "lg:pl-[29%]", "xl:pl-[31%]");
      const embla = window.EmblaCarousel(viewport, {
        align: "center",
        loop: region.dataset.loop === "true",
        containScroll: false,
        duration: 32,
        skipSnaps: true,
        dragThreshold: 8,
      });

      const FEATURED = "text-[clamp(0.95rem,4vw,1.4rem)]";
      const REGULAR = "text-[clamp(0.9rem,1.1vw,1.05rem)]";

      const onSelect = () => {
        const selected = embla.selectedScrollSnap();
        slides.forEach((slide, i) => {
          const active = i === selected;
          $("[data-card-cta]", slide)?.classList.toggle("hidden", !active);
          $("[data-card-status]", slide)?.classList.toggle("hidden", !active);
          const title = $("[data-card-title]", slide);
          title?.classList.toggle(FEATURED, active);
          title?.classList.toggle(REGULAR, !active);
          const price = $("[data-card-price]", slide);
          price?.classList.toggle("text-foreground", active);
          price?.classList.toggle("text-muted-foreground", !active);
        });
        bars.forEach((bar, i) => {
          bar.classList.toggle("bg-primary", i === selected);
          bar.classList.toggle("bg-border", i !== selected);
        });
        if (counter) counter.textContent = `${pad(selected + 1)} / ${total}`;
        if (viewLink && slides[selected]) viewLink.href = slides[selected].dataset.url;
      };

      // The portrait responds continuously to swipe position, not just the final snap.
      let frame = 0;
      const updateDepth = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const rect = embla.rootNode().getBoundingClientRect();
          const center = rect.left + rect.width / 2;
          const slideWidth = embla.slideNodes()[0]?.getBoundingClientRect().width || 1;
          embla.slideNodes().forEach((slide) => {
            const r = slide.getBoundingClientRect();
            const distance = Math.abs(r.left + r.width / 2 - center) / slideWidth;
            slide.style.setProperty("--carousel-focus", String(Math.max(0, 1 - distance)));
          });
        });
      };

      embla.on("select", onSelect);
      embla.on("reInit", onSelect);
      embla.on("scroll", updateDepth);
      embla.on("reInit", updateDepth);
      window.addEventListener("resize", updateDepth);
      onSelect();
      updateDepth();

      // Tapping a side piece centers it; Embla suppresses clicks after real drags.
      slides.forEach((slide, index) => {
        slide.addEventListener(
          "click",
          (e) => {
            if (index !== embla.selectedScrollSnap()) {
              e.preventDefault();
              e.stopPropagation();
              embla.scrollTo(index);
            }
          },
          true,
        );
      });

      $("[data-carousel-prev]", region)?.addEventListener("click", () => embla.scrollPrev());
      $("[data-carousel-next]", region)?.addEventListener("click", () => embla.scrollNext());
      region.addEventListener("keydown", (e) => {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          embla.scrollPrev();
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          embla.scrollNext();
        }
      });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Shop product-type filter — shop.tsx                               */
  /* ---------------------------------------------------------------- */
  function initTypeFilters(root) {
    $$("[data-type-filters]", root).forEach((bar) => {
      if (!once(bar, "init")) return;
      const buttons = $$("[data-type-filter]", bar);
      bar.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-type-filter]");
        if (!btn) return;
        const type = btn.dataset.typeFilter;
        buttons.forEach((b) => {
          const active = b === btn;
          b.classList.toggle("text-primary", active);
          b.classList.toggle("text-muted-foreground", !active);
          b.classList.toggle("hover:text-foreground", !active);
        });
        $$("[data-product-type]").forEach((card) => {
          const show = type === "all" || card.dataset.productType === type;
          card.classList.toggle("hidden", !show);
          if (show) card.classList.add("reveal-in");
        });
      });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Newsletter — client-side validation like Newsletter.tsx           */
  /* ---------------------------------------------------------------- */
  function initNewsletter(root) {
    $$("[data-newsletter]", root).forEach((form) => {
      if (!once(form, "init")) return;
      const input = $('input[type="email"]', form);
      const error = $("[data-newsletter-error]", form);
      form.addEventListener("submit", (e) => {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value)) {
          e.preventDefault();
          error?.classList.remove("hidden");
        }
      });
      input?.addEventListener("input", () => error?.classList.add("hidden"));
    });
  }

  /* ---------------------------------------------------------------- */
  /* Global event delegation                                           */
  /* ---------------------------------------------------------------- */
  document.addEventListener("click", (e) => {
    const t = e.target;
    if (t.closest("[data-menu-open]")) setMenu(true);
    if (t.closest("[data-menu-close]")) setMenu(false);
    if (t.closest("[data-search-open]")) {
      e.preventDefault();
      setSearch(true);
    }
    if (t.closest("[data-search-close]")) setSearch(false);
    if (t.closest("[data-cart-open]")) {
      e.preventDefault();
      setCart(true);
    }
    if (t.closest("[data-cart-close]")) setCart(false);

    const qty = t.closest("[data-cart-qty]");
    if (qty) {
      const line = Number(qty.closest("[data-cart-line]").dataset.cartLine);
      changeLine(line, Math.max(0, Number(qty.dataset.cartQty)));
    }
    // Close the mobile menu after following one of its links.
    if (t.closest("[data-mobile-menu] a")) setMenu(false);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    setSearch(false);
    setCart(false);
    setMenu(false);
  });

  document.addEventListener("input", (e) => {
    if (!e.target.matches("[data-search-input]")) return;
    clearTimeout(searchTimer);
    const value = e.target.value;
    searchTimer = setTimeout(() => runSearch(value), 180);
  });

  // Language picker submits Shopify's localization form immediately.
  document.addEventListener("change", (e) => {
    if (e.target.matches("[data-localization-select]")) e.target.form.submit();
  });

  function init(root = document) {
    initHeroVideo(root);
    initAnnouncements(root);
    initReveal(root);
    initProduct(root);
    initQuickAdd(root);
    initCarousels(root);
    initTypeFilters(root);
    initNewsletter(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  // Theme editor: re-run for sections added or re-rendered in place.
  document.addEventListener("shopify:section:load", (e) => init(e.target));
  // Newsletter posts reload the page; land back on the form.
  if (window.location.search.includes("customer_posted=true")) {
    document.getElementById("NewsletterForm")?.scrollIntoView({ block: "center" });
  }
})();
